import React, { useEffect, useState } from 'react';
import { Button, Spin } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../redux/hooks';
import { setAuthenticated } from '../../redux/reducers/authSlice';
import Api from '../../services/api';
import {
  MOBILE_WEB_SSO_CLIENT_ID,
  clearSsoSession,
  consumeSsoPkce,
  getSsoCallbackUrl,
} from '../../services/mobileSso';
import './LoginScreen.css';

/** Survives React Strict Mode remounts within the same page load. */
let exchangeInFlight: Promise<void> | null = null;

const AuthCallbackScreen: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [message, setMessage] = useState('Signing you in…');
  const [subMessage, setSubMessage] = useState('Finishing sign-in with your organization');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const error = searchParams.get('error');
    if (error === 'access_denied') {
      clearSsoSession();
      navigate('/auth/sso-unavailable', { replace: true });
      return;
    }

    if (exchangeInFlight) {
      return;
    }

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const pkce = consumeSsoPkce();

    if (!code || !pkce || (state && state !== pkce.state)) {
      clearSsoSession();
      setFailed(true);
      setMessage("Can't complete sign-in");
      setSubMessage('The sign-in session expired or was invalid. Please try again.');
      return;
    }

    exchangeInFlight = (async () => {
      try {
        const result = await Api.exchangeSsoToken({
          code,
          codeVerifier: pkce.verifier,
          redirectUri: getSsoCallbackUrl(),
          clientId: MOBILE_WEB_SSO_CLIENT_ID,
        });
        setMessage("You're signed in");
        setSubMessage('Redirecting to your dashboard…');
        dispatch(setAuthenticated(result));
        navigate('/home', { replace: true });
      } catch {
        clearSsoSession();
        setFailed(true);
        setMessage("Can't complete sign-in");
        setSubMessage(
          'Unable to finish SSO (token exchange failed). Check that the API allows this site (CORS), then try again or sign in with your password.'
        );
      } finally {
        exchangeInFlight = null;
      }
    })();
  }, [searchParams, navigate, dispatch]);

  return (
    <div className="login-container sso-status-screen">
      <div className="sso-status-content">
        {!failed && <Spin size="large" />}
        <h1 className="sso-status-title">{message}</h1>
        <p className="sso-status-body">{subMessage}</p>
        {failed && (
          <Button type="primary" onClick={() => navigate('/login', { replace: true })}>
            Back to sign in
          </Button>
        )}
      </div>
    </div>
  );
};

export default AuthCallbackScreen;
